// Landing + organizer home (design "Login" + "Home" screens). Sign-in is
// OIDC (Casdoor) — the design's credential form maps to a single sign-in
// CTA; identity lives on the org's identity server. Hero claims are the
// HONEST ones (F-SEC-3): no invented compliance badges.

import { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import logoUrl from '../assets/brizina-logo.svg';
import { api, loginUrl, MeetingSummary, signupUrl } from '../api';
import { useShell } from '../components/AppShell';
import { ScheduleDrawer } from '../components/ScheduleDrawer';
import { pushToast } from '../store/callStore';
import { Button } from '../ui/Button';
import { TextInput } from '../ui/Field';
import {
  IconCalendar,
  IconCamera,
  IconChevronLeft,
  IconChevronRight,
  IconInstant,
  IconSchedule,
  IconZeroKnowledge,
} from '../ui/icons';

// A high-entropy, human-shareable passphrase for Model B links.
function generatePassphrase(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '').slice(0, 24);
}

export function HomePage() {
  const { me } = useShell(); // session + chrome belong to the shell
  if (!me) return <Landing />;
  return <Dashboard />;
}

function Landing() {
  return (
    <div className="bz-auth">
      <div className="bz-auth-hero">
        <img src={logoUrl} alt="Brizina" className="bz-logo bz-logo-lg" />
        <div>
          <h1 className="bz-auth-hero-title">Meetings that feel like being in the room.</h1>
          <p className="bz-auth-hero-sub">
            Secure, crisp video calls for teams that move fast. No downloads, no friction.
          </p>
        </div>
        <div className="bz-auth-hero-marks">
          <span>End-to-end encrypted</span>
          <span>Self-hosted</span>
          <span>Zero-knowledge mode</span>
        </div>
        <div className="bz-auth-hero-glow" />
      </div>
      <div className="bz-auth-form">
        <div className="bz-auth-card">
          <img src={logoUrl} alt="Brizina" className="bz-logo bz-auth-logo" />
          <h2 className="bz-auth-title">Welcome back</h2>
          <p className="bz-auth-sub">Sign in to your Brizina workspace</p>
          <a className="bz-btn bz-btn-primary bz-btn-block" href={loginUrl}>
            Sign in
          </a>
          <div className="bz-auth-alt">
            <span>New to Brizina?</span>
            <a href={signupUrl}>Create an account</a>
          </div>
          <p className="bz-auth-note">
            Joining a meeting as a guest needs no account — just open the invite link.
          </p>
          <footer className="bz-footer">
            <Link to="/security">Security</Link>
          </footer>
        </div>
      </div>
    </div>
  );
}

const DAY_MS = 86_400_000;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** The Sunday-anchored week containing `d`, as seven dates. */
function weekOf(d: Date): Date[] {
  const first = startOfDay(d);
  first.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 7 }, (_, i) => new Date(first.getTime() + i * DAY_MS));
}

function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/** Compact 24h stamp — 18H, 19H30 (the house style from the design). */
function stamp(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  return m === 0 ? `${h}H` : `${h}H${String(m).padStart(2, '0')}`;
}

/** "18H - 19H30", or just the start when no end time was set. */
function slotLabel(m: MeetingSummary): string {
  const start = new Date(m.scheduledAt!);
  if (!m.endsAt) return stamp(start);
  return `${stamp(start)} - ${stamp(new Date(m.endsAt))}`;
}

/** Live right now: between start and end (or within an hour of an open-ended
 * start). This drives the "Now" badge — the only urgent thing on the page. */
function isLive(m: MeetingSummary, now: number): boolean {
  const start = new Date(m.scheduledAt!).getTime();
  const end = m.endsAt ? new Date(m.endsAt).getTime() : start + 60 * 60 * 1000;
  return now >= start && now <= end;
}

function meetingsOn(meetings: MeetingSummary[], day: Date): MeetingSummary[] {
  return meetings
    .filter((m) => m.scheduledAt && sameDay(new Date(m.scheduledAt), day))
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime());
}

function Dashboard() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [meetings, setMeetings] = useState<MeetingSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api
      .listMeetings()
      .then(setMeetings)
      .catch(() => setMeetings([]));
  }, [refresh]);

  // Selected day drives the list; the strip pages a week at a time.
  const [selected, setSelected] = useState(() => startOfDay(new Date()));
  const week = useMemo(() => weekOf(selected), [selected]);
  const dayMeetings = useMemo(() => meetingsOn(meetings, selected), [meetings, selected]);
  // A single tick per minute keeps the "Now" badge honest without re-rendering
  // anything else on the page.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const pickDay = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const iso = e.currentTarget.dataset.day;
    if (iso) setSelected(startOfDay(new Date(iso)));
  }, []);
  const prevWeek = useCallback(
    () => setSelected((d) => new Date(d.getTime() - 7 * DAY_MS)),
    [],
  );
  const nextWeek = useCallback(
    () => setSelected((d) => new Date(d.getTime() + 7 * DAY_MS)),
    [],
  );

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const closeMenus = useCallback(() => setMenuOpen(false), []);

  // Instant meeting: create it, then go straight in as host (the lobby is for
  // people who join a meeting, not the person who started it). The in-call
  // share card handles inviting others.
  const startNow = useCallback(() => {
    setMenuOpen(false);
    setBusy(true);
    api
      .createMeeting()
      .then((c) => navigate(c.joinPath, { state: { justCreated: true } }))
      .catch((e: unknown) => pushToast(e instanceof Error ? e.message : 'Failed', 'error'))
      .finally(() => setBusy(false));
  }, [navigate]);

  const startZeroKnowledge = useCallback(() => {
    setMenuOpen(false);
    setBusy(true);
    // Model B: the passphrase lives ONLY in the client and the shareable link
    // fragment (#k=…) — never sent to a server (§5.2).
    const secret = generatePassphrase();
    api
      .createMeeting({ e2eeModel: 'b' })
      .then((c) =>
        navigate(`${c.joinPath}#k=${encodeURIComponent(secret)}`, {
          state: { justCreated: true },
        }),
      )
      .catch((e: unknown) => pushToast(e instanceof Error ? e.message : 'Failed', 'error'))
      .finally(() => setBusy(false));
  }, [navigate]);

  const openSchedule = useCallback(() => {
    setMenuOpen(false);
    setScheduleOpen(true);
  }, []);
  const closeSchedule = useCallback(() => setScheduleOpen(false), []);
  const onScheduled = useCallback(() => setRefresh((n) => n + 1), []);

  const onJoinCode = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setJoinCode(e.target.value);
  }, []);

  const joinByCode = useCallback(() => {
    const raw = joinCode.trim();
    if (!raw) return;
    // Accept a bare code or a full link (keep any #k= fragment for Model B).
    try {
      const url = new URL(raw);
      void navigate(url.pathname + url.hash);
      return;
    } catch {
      /* not a URL — treat as a code */
    }
    void navigate(`/m/${raw.replace(/^\/?m\//, '')}`);
  }, [joinCode, navigate]);

  const joinUpcoming = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const slug = e.currentTarget.dataset.slug;
      if (slug) void navigate(`/m/${slug}`);
    },
    [navigate],
  );

  return (
    <>
      <div className="bz-shell-col">
          <div className="bz-home-hero">
            <h1 className="bz-home-title">
              Premium video meetings.
              <br />
              Now for everyone.
            </h1>
            <p className="bz-home-sub">
              Start a meeting instantly or schedule one for later — your team joins with a single
              link. Every meeting is end-to-end encrypted.
            </p>
            <div className="bz-home-actions">
              <div className="bz-newmeeting">
                <Button variant="primary" onClick={toggleMenu} disabled={busy}>
                  <IconCamera size={17} />
                  New meeting
                </Button>
                {menuOpen && (
                  <div className="bz-menu bz-newmeeting-menu" role="menu" onMouseLeave={closeMenus}>
                    <button type="button" className="bz-menu-item" onClick={startNow}>
                      <IconInstant size={16} />
                      Start an instant meeting
                    </button>
                    <button type="button" className="bz-menu-item" onClick={startZeroKnowledge}>
                      <IconZeroKnowledge size={16} />
                      Zero-knowledge meeting
                    </button>
                    <button type="button" className="bz-menu-item" onClick={openSchedule}>
                      <IconSchedule size={16} />
                      Schedule for later
                    </button>
                  </div>
                )}
              </div>
              {/* Input + Join read as ONE control, like the design. */}
              <div className="bz-joinrow">
                <TextInput
                  placeholder="Enter a code or link"
                  value={joinCode}
                  onChange={onJoinCode}
                  aria-label="Meeting code or link"
                />
                <button
                  type="button"
                  className="bz-join-go"
                  onClick={joinByCode}
                  disabled={joinCode.trim() === ''}
                >
                  Join
                </button>
              </div>
            </div>
          </div>

          <div className="bz-daystrip">
            <Link to="/calendar" className="bz-daystrip-date">
              {selected.toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
              <IconCalendar size={15} />
            </Link>
            <div className="bz-daystrip-week">
              <button
                type="button"
                className="bz-daystrip-nav"
                onClick={prevWeek}
                aria-label="Previous week"
              >
                <IconChevronLeft size={15} />
              </button>
              {week.map((d) => {
                const on = sameDay(d, selected);
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    data-day={d.toISOString()}
                    onClick={pickDay}
                    className={on ? 'bz-daychip bz-daychip-on' : 'bz-daychip'}
                    aria-pressed={on}
                  >
                    <span className="bz-daychip-wd">
                      {d.toLocaleDateString(undefined, { weekday: 'short' })}
                    </span>
                    <span className="bz-daychip-num">{d.getDate()}</span>
                  </button>
                );
              })}
              <button
                type="button"
                className="bz-daystrip-nav"
                onClick={nextWeek}
                aria-label="Next week"
              >
                <IconChevronRight size={15} />
              </button>
            </div>
          </div>

          <div className="bz-daylist">
            {dayMeetings.length === 0 && (
              <p className="bz-empty">Nothing scheduled for this day.</p>
            )}
            {dayMeetings.map((m) => (
              <div key={m.slug} className="bz-daylist-row">
                <div className="bz-daylist-main">
                  <div className="bz-daylist-time">
                    {slotLabel(m)}
                    {isLive(m, nowTick) && <span className="bz-badge-now">Now</span>}
                  </div>
                  <div className="bz-daylist-title">{m.title ?? m.slug}</div>
                </div>
                <Button size="sm" variant="primary" data-slug={m.slug} onClick={joinUpcoming}>
                  Join
                </Button>
              </div>
            ))}
        </div>
      </div>

      {scheduleOpen && <ScheduleDrawer onClose={closeSchedule} onCreated={onScheduled} />}
    </>
  );
}
